import { ajax } from 'OK/utils/vanilla';
import logger from 'OK/logger';

interface IWebApiOptions {
    filter?: string;
    signal?: AbortSignal;
}

interface IWebApiRequest {
    id: number;
    parameters?: {};
    filter?: string;
}

interface IWebApiResponse<R> {
    result: R;
    success: boolean;
    error: boolean;
}

const BASE_URL = '/web-api/v2';
const COMMUNICATION_ERROR_CODE = 'api.communication-error';

let lastRequestId = 0;
let communicationError = false;

function generateId(): number {
    return ++lastRequestId;
}

function getUrl(endpoint: string): string {
    return `${BASE_URL}/${endpoint}`;
}

function createBody(request: IWebApiRequest): string {
    return JSON.stringify(request, null, 2);
}

function send<R = any>(endpoint: string, request: IWebApiRequest, options: IWebApiOptions): Promise<R> {
    const url = getUrl(endpoint);
    const data = createBody(request);

	return new Promise(function (resolve, reject) {

        const request = ajax<IWebApiResponse<R>>({
            contentType: 'application/json; charset=utf-8',
            url,
            dataType: 'json',
            data
        });

		const xhr = request.xhr;

		function onAbort() {
			xhr.abort();

			communicationError = true;

			reset();

			reject({
				type: 2,
				typeName: 'ABORT',
				errorCode: 'abort',
				errorMessage: xhr.statusText || 'abort'
			});
		}

		function reset() {
			if (options.signal) {
				options.signal.removeEventListener('abort', onAbort);
			}
		}

		if (options.signal) {
			options.signal.addEventListener('abort', onAbort);
		}

		request.then(function (data) {
			communicationError = false;

			reset();
            const response = data.response;

            if (response.success) {
                resolve(response.result);
            } else {
                reject(response.error);
            }

        }, function (xhr){
			// Если сеть падает первый раз, то сообщаем про это,
			// писать остальные падения смысла нет
			// При восстановлении сети состояние ошибки сбросится
        	if (!communicationError) {
				logger.error(
					COMMUNICATION_ERROR_CODE,
					endpoint,
					xhr.status
				);

				window.OK.Tracer.error(
					new Error(`${xhr.status} error in endpoint=${endpoint} with message=${xhr.statusText}`)
				);
        	}

			reset();

        	communicationError = true;

			reject({
				type: 200,
				typeName: 'SERVER_ERROR',
				errorCode: COMMUNICATION_ERROR_CODE,
				errorMessage: xhr.statusText || COMMUNICATION_ERROR_CODE
			});
        });
    });
}

export default class WebApi {
    public static invoke<R = any>(endpoint: string, parameters? : {}, options: IWebApiOptions = {}): Promise<R> {
        const request: IWebApiRequest = {
            id: generateId()
        };

		const filter = options.filter;

        if (parameters !== undefined && parameters !== null) {
            request.parameters = parameters;
        }

        if (filter !== undefined && filter !== null) {
            request.filter = filter;
        }

        return send(endpoint, request, options);
    }
}

// Определяем глобальный стор конфигурации
if (typeof window !== 'undefined') {
    window.webapi = WebApi;
}

export const invoke = WebApi.invoke;
